/**
 * Registro de eventos exportable (doc. 04 §13; paso 1.6, avance 4): **JSON Lines** (un evento
 * completo por línea, para guardar o analizar con herramientas de minería de procesos) y **CSV**
 * (una fila por evento, columnas planas, que abre en una hoja de cálculo).
 *
 * La marca de tiempo (`marcaTiempo`) se deriva de una fecha base ficticia (doc. 04 §13.1): el día 1
 * del horizonte es el lunes 1 de marzo de 2027, en hora de Lima (UTC−5, sin horario de verano). Se
 * calcula sin el reloj del sistema, así el mismo registro siempre da el mismo archivo.
 */

import type { Evento } from '../dominio/ejecucion';
import { diaDe, segundoDelDia } from '../dominio/tiempo';

/** Fecha ficticia del día 1 del horizonte y zona horaria de Lima. */
export const FECHA_BASE = { anio: 2027, mes: 3, dia: 1 } as const;
const ZONA = '-05:00';

/** Días desde el 1970-01-01 de una fecha civil (algoritmo de calendario gregoriano proléptico). */
function diasDesdeEpoca(anio: number, mes: number, dia: number): number {
  const a = mes <= 2 ? anio - 1 : anio;
  const era = Math.floor(a / 400);
  const ae = a - era * 400;
  const dda = Math.floor((153 * (mes + (mes > 2 ? -3 : 9)) + 2) / 5) + dia - 1;
  const dde = ae * 365 + Math.floor(ae / 4) - Math.floor(ae / 100) + dda;
  return era * 146097 + dde - 719468;
}

/** Fecha civil de un número de días desde el 1970-01-01. */
function fechaDesdeDias(z0: number): { anio: number; mes: number; dia: number } {
  const z = z0 + 719468;
  const era = Math.floor(z / 146097);
  const dde = z - era * 146097;
  const ae = Math.floor(
    (dde - Math.floor(dde / 1460) + Math.floor(dde / 36524) - Math.floor(dde / 146096)) / 365,
  );
  const dda = dde - (365 * ae + Math.floor(ae / 4) - Math.floor(ae / 100));
  const mp = Math.floor((5 * dda + 2) / 153);
  const dia = dda - Math.floor((153 * mp + 2) / 5) + 1;
  const mes = mp + (mp < 10 ? 3 : -9);
  return { anio: ae + era * 400 + (mes <= 2 ? 1 : 0), mes, dia };
}

const dos = (n: number) => String(n).padStart(2, '0');

/** «08:22:00» para un instante del horizonte. */
export function horaDelDia(t: number): string {
  const s = segundoDelDia(t);
  return `${dos(Math.floor(s / 3600))}:${dos(Math.floor((s % 3600) / 60))}:${dos(s % 60)}`;
}

/** Marca de tiempo ISO 8601 con zona («2027-03-02T08:22:00-05:00») de un instante del horizonte. */
export function marcaTiempo(t: number): string {
  const base = diasDesdeEpoca(FECHA_BASE.anio, FECHA_BASE.mes, FECHA_BASE.dia);
  const f = fechaDesdeDias(base + diaDe(t) - 1);
  return `${f.anio}-${dos(f.mes)}-${dos(f.dia)}T${horaDelDia(t)}${ZONA}`;
}

/** JSON Lines: cada evento completo en una línea, con su marca de tiempo derivada. */
export function registroJsonLines(eventos: readonly Evento[]): string {
  return (
    eventos.map((e) => JSON.stringify({ ...e, marcaTiempo: marcaTiempo(e.t) })).join('\n') + '\n'
  );
}

/** Columnas del CSV, en orden (doc. 04 §13.4: caso = OT, actividad = etapa, recurso, marca). */
export const COLUMNAS_CSV = [
  'id',
  'fuente',
  'ejecucionId',
  'marcaTiempo',
  't',
  'dia',
  'hora',
  'tipo',
  'unidadId',
  'etapaId',
  'categoria',
  'estacionId',
  'recursos',
  'motivo',
  'origen',
  'destino',
  'distanciaM',
  'duracionS',
  'atributos',
] as const;

/** Un valor como celda CSV (RFC 4180): entre comillas si lleva coma, comillas o salto de línea. */
function celda(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

/**
 * CSV con una fila por evento y columnas planas, separado por comas y con punto decimal; empieza con
 * la marca de orden de bytes (BOM) para que la hoja de cálculo lea bien las tildes y usa fin de línea
 * CRLF. Los recursos van como «bahia B03; tecnico T07» y los atributos como JSON.
 */
export function registroCsv(eventos: readonly Evento[]): string {
  const filas = [COLUMNAS_CSV.join(',')];
  for (const e of eventos) {
    const valores: Record<(typeof COLUMNAS_CSV)[number], string | number | null | undefined> = {
      id: e.id,
      fuente: e.fuente,
      ejecucionId: e.ejecucionId,
      marcaTiempo: marcaTiempo(e.t),
      t: e.t,
      dia: e.dia,
      hora: horaDelDia(e.t),
      tipo: e.tipo,
      unidadId: e.unidadId,
      etapaId: e.etapaId,
      categoria: e.categoria,
      estacionId: e.ubicacion?.estacionId,
      recursos: e.recursos?.map((r) => `${r.tipo} ${r.id}`).join('; '),
      motivo: e.motivo?.join('; '),
      origen: e.traslado?.origen,
      destino: e.traslado?.destino,
      distanciaM: e.traslado?.distanciaM,
      duracionS: e.traslado?.duracionS,
      atributos:
        e.atributos && Object.keys(e.atributos).length > 0 ? JSON.stringify(e.atributos) : '',
    };
    filas.push(COLUMNAS_CSV.map((c) => celda(valores[c])).join(','));
  }
  return '﻿' + filas.join('\r\n') + '\r\n';
}
